// The example-payload logic behind OrgTriggerModalComponent, kept free of Angular and service
// imports so it can be unit-tested directly - importing the component pulls in OrganigramApiService
// and CustomerdatabaseService, whose import cycle aborts the Karma bootstrap. Same reason
// organigram-hierarchy.ts and org-standard-v2.graph.ts are separate modules.
//
// TEMPORARY, with the rest of the trigger scaffolding.
//
// This file used to hold {{triggerData.x}} template machinery: the user declared the fields their
// webhook would post, then spliced placeholders into the message. That was removed on purpose.
// The recipient is an AGENT - it reads the payload perfectly well on its own, so interpolating
// fields into a sentence was the user doing the model's job, and it required them to know their
// payload schema and to write template syntax. Neither is reasonable for a non-technical user.
//
// What replaced it: the message stays plain English, the payload rides along as context, and the
// user optionally pastes ONE example payload. Pasting is copy-paste, not authoring - and the
// fields below are DERIVED from what they pasted rather than declared by hand.

export interface ParsedExample {
  ok: boolean;
  // Present only when ok. Deliberately `any`: a payload is arbitrary JSON.
  value?: any;
  // Present only when it failed - phrased for someone who has never seen a parser message.
  error?: string;
}

// Parse the pasted example. Blank is VALID and means "no example": a schedule or a manual run has
// no inbound body, and even a webhook is allowed to be configured before a sample is to hand.
export function parseExampleData(text: string): ParsedExample {
  const trimmed = (text || '').trim();
  if (!trimmed) { return { ok: true, value: null }; }
  let parsed: any;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // JSON.parse's own message ("Unexpected token } in JSON at position 42") means nothing to the
    // person this form is for, so it is replaced rather than surfaced.
    return { ok: false, error: 'That does not look like valid data. Paste the whole example, including the outer { }.' };
  }
  // A bare string or number parses fine but carries no fields, so the agent would receive a value
  // with nothing to call it. Named early rather than left to confuse at fire time.
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'The example needs to be a set of fields, like { "email": "anna@acme.com" }.' };
  }
  return { ok: true, value: parsed };
}

// The field names in a parsed example, top level only. Shown back to the user as "the agent will
// see: email, name, company" - the same information the old declare-your-variables list carried,
// except nobody had to type it and it cannot disagree with the real payload.
//
// Top level only on purpose: a nested object is still handed to the agent whole, and flattening
// would produce a list long enough to stop being reassuring, which is this list's only job.
export function payloadFieldNames(value: any): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) { return []; }
  return Object.keys(value);
}

// What the agent actually receives: the instruction, then the payload as context. Built here so
// the preview in the form and the eventual fire path cannot drift apart - the preview is only
// honest if it is produced by the same function that sends.
export function composeAgentMessage(message: string, payload: any): string {
  const instruction = (message || '').trim();
  if (payload === null || payload === undefined) { return instruction; }
  return `${instruction}\n\nData:\n${JSON.stringify(payload, null, 2)}`;
}
